import React from "react";
import { NeoModal, NeoButton, NeoInput } from "../../../../../components/Common";
import { WORKOUT_STYLES, WORKOUT_TYPES } from "../../constants";
import { AI_OBJECTIVES_BY_TYPE, AI_LEVELS, AI_DURATIONS } from "../../hooks/useAiWorkout";
import { useTenant } from "../../../../../hooks/useTenant";

const BRANCH_TO_WORKOUT_TYPE = {
    musculacao: "strength",
    crossfit: "wod",
    natacao: "swimming",
    corrida: "running",
};

/**
 * AiWorkoutModal
 * Modal para configurar e disparar a geração de treino via IA.
 */
const AiWorkoutModal = ({
    isOpen,
    toggle,
    formData,
    updateFormField,
    toggleStyle,
    availableProviders,
    loadingProviders,
    isGenerating,
    error,
    onGenerate,
}) => {
    const { primaryBranch = [] } = useTenant();
    const hasProviders = availableProviders.length > 0;

    const allowedWorkoutTypes = primaryBranch.length > 0
        ? primaryBranch.map(b => BRANCH_TO_WORKOUT_TYPE[b]).filter(Boolean)
        : null;
    const visibleWorkoutTypes = allowedWorkoutTypes
        ? WORKOUT_TYPES.filter(t => allowedWorkoutTypes.includes(t.value))
        : WORKOUT_TYPES;

    const footerActions = (
        <>
            <NeoButton
                variant="light"
                className="me-2"
                onClick={toggle}
                disabled={isGenerating}
            >
                Cancelar
            </NeoButton>
            <NeoButton
                variant="primary"
                onClick={onGenerate}
                disabled={isGenerating || !hasProviders || !formData.provider}
                loading={isGenerating}
                loadingText="Gerando treino..."
                icon="mdi-auto-fix"
            >
                Gerar Treino
            </NeoButton>
        </>
    );

    return (
        <NeoModal
            isOpen={isOpen}
            toggle={!isGenerating ? toggle : () => {}}
            title="Gerar Treino com IA"
            subtitle="Preencha as características desejadas"
            headerIcon="mdi-robot-outline"
            size="lg"
            centered
            backdrop="static"
            footerActions={footerActions}
        >
            {/* Alerta de provider não configurado */}
            {!loadingProviders && !hasProviders && (
                <div className="alert alert-warning d-flex align-items-start gap-2 mb-4" role="alert">
                    <i className="mdi mdi-alert-circle-outline font-size-20 mt-0"></i>
                    <div>
                        <strong className="d-block font-size-13">Nenhuma IA configurada</strong>
                        <p className="mb-0 small text-muted">
                            Configure uma integração com OpenAI ou Gemini em{" "}
                            <strong>Configurações &gt; Integrações</strong> antes de gerar treinos.
                        </p>
                    </div>
                </div>
            )}

            {error && (
                <div className="alert alert-danger d-flex align-items-start gap-2 mb-3" role="alert">
                    <i className="mdi mdi-alert-outline font-size-18 mt-0"></i>
                    <span className="small">{error}</span>
                </div>
            )}

            {loadingProviders ? (
                <div className="text-center py-5">
                    <i className="mdi mdi-loading mdi-spin text-primary font-size-28 d-block mb-2" />
                    <p className="text-muted small mb-0">Verificando integrações...</p>
                </div>
            ) : (
                <div className={!hasProviders ? "opacity-50 pe-none" : ""}>
                    <div className="row">
                        {/* Provider de IA */}
                        <div className="col-md-6">
                            <div className="form-group mb-3">
                                <label className="form-label font-size-10 fw-bold text-uppercase text-muted mb-1">
                                    Provider de IA <span className="text-danger">*</span>
                                </label>
                                <NeoInput
                                    type="select"
                                    value={formData.provider}
                                    onChange={(e) => updateFormField("provider", e.target.value)}
                                    disabled={isGenerating}
                                    size="sm"
                                >
                                    <option value="">Selecione...</option>
                                    {availableProviders.map((p) => (
                                        <option key={p.id} value={p.id}>
                                            {p.name} ({p.model})
                                        </option>
                                    ))}
                                </NeoInput>
                            </div>
                        </div>

                        {/* Modalidade */}
                        <div className="col-md-6">
                            <div className="form-group mb-3">
                                <label className="form-label font-size-10 fw-bold text-uppercase text-muted mb-1">
                                    Modalidade <span className="text-danger">*</span>
                                </label>
                                <NeoInput
                                    type="select"
                                    value={formData.workoutType}
                                    onChange={(e) => {
                                        const newType = e.target.value;
                                        const firstObjective = AI_OBJECTIVES_BY_TYPE[newType]?.[0]?.value || "";
                                        let newVolume = 2000;
                                        if (newType === "strength" || newType === "running") newVolume = 5;
                                        else if (newType === "wod") newVolume = 15;
                                        
                                        updateFormField("workoutType", newType);
                                        updateFormField("objective", firstObjective);
                                        updateFormField("volume", newVolume);
                                    }}
                                    disabled={isGenerating}
                                    size="sm"
                                >
                                    {visibleWorkoutTypes.map((t) => (
                                        <option key={t.value} value={t.value}>{t.label}</option>
                                    ))}
                                </NeoInput>
                            </div>
                        </div>

                        {/* Objetivo */}
                        <div className="col-md-6">
                            <div className="form-group mb-3">
                                <label className="form-label font-size-10 fw-bold text-uppercase text-muted mb-1">
                                    Objetivo <span className="text-danger">*</span>
                                </label>
                                <NeoInput
                                    type="select"
                                    value={formData.objective}
                                    onChange={(e) => updateFormField("objective", e.target.value)}
                                    disabled={isGenerating}
                                    size="sm"
                                >
                                    {(AI_OBJECTIVES_BY_TYPE[formData.workoutType || 'swimming'] || []).map((o) => (
                                        <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                </NeoInput>
                            </div>
                        </div>

                        {/* Nível */}
                        <div className="col-md-6">
                            <div className="form-group mb-3">
                                <label className="form-label font-size-10 fw-bold text-uppercase text-muted mb-1">
                                    Nível dos alunos <span className="text-danger">*</span>
                                </label>
                                <NeoInput
                                    type="select"
                                    value={formData.level}
                                    onChange={(e) => updateFormField("level", e.target.value)}
                                    disabled={isGenerating}
                                    size="sm"
                                >
                                    {AI_LEVELS.map((l) => (
                                        <option key={l.value} value={l.value}>{l.label}</option>
                                    ))}
                                </NeoInput>
                            </div>
                        </div>

                        {/* Volume */}
                        <div className="col-md-6">
                            <div className="form-group mb-3">
                                <label className="form-label font-size-10 fw-bold text-uppercase text-muted mb-1">
                                    {formData.workoutType === 'strength' ? 'Exercícios por dia' : 'Volume desejado'} <span className="text-danger">*</span>
                                </label>
                                <div className="input-group input-group-sm">
                                    <input
                                        type="number"
                                        value={formData.volume}
                                        onChange={(e) => updateFormField("volume", e.target.value)}
                                        disabled={isGenerating}
                                        min="1"
                                        className="form-control"
                                        placeholder="Ex: 2000, 5, 20"
                                    />
                                    <span className="input-group-text bg-light text-muted font-size-11">
                                        {formData.workoutType === 'swimming' ? 'metros' : 
                                         formData.workoutType === 'running' ? 'km' : 
                                         formData.workoutType === 'wod' ? 'minutos' : 'exercícios'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Duração */}
                        <div className="col-md-6">
                            <div className="form-group mb-3">
                                <label className="form-label font-size-10 fw-bold text-uppercase text-muted mb-1">
                                    Duração da aula
                                </label>
                                <NeoInput
                                    type="select"
                                    value={formData.duration}
                                    onChange={(e) => updateFormField("duration", e.target.value)}
                                    disabled={isGenerating}
                                    size="sm"
                                >
                                    <option value="">Livre</option>
                                    {AI_DURATIONS.map((d) => (
                                        <option key={d.value} value={d.value}>{d.label}</option>
                                    ))}
                                </NeoInput>
                            </div>
                        </div>

                        {/* Dias por Semana e Semanas (Apenas para musculação e corrida) */}
                        {(formData.workoutType === 'strength' || formData.workoutType === 'running') && (
                            <>
                                <div className="col-md-6">
                                    <div className="form-group mb-3">
                                        <label className="form-label font-size-10 fw-bold text-uppercase text-muted mb-1">
                                            Dias por semana
                                        </label>
                                        <NeoInput
                                            type="number"
                                            value={formData.daysPerWeek || 3}
                                            onChange={(e) => updateFormField("daysPerWeek", e.target.value)}
                                            disabled={isGenerating}
                                            min={1}
                                            max={7}
                                            size="sm"
                                        />
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-group mb-3">
                                        <label className="form-label font-size-10 fw-bold text-uppercase text-muted mb-1">
                                            Semanas (Ciclo)
                                        </label>
                                        <NeoInput
                                            type="number"
                                            value={formData.weeks || 4}
                                            onChange={(e) => updateFormField("weeks", e.target.value)}
                                            disabled={isGenerating}
                                            min={1}
                                            max={12}
                                            size="sm"
                                        />
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Estilos preferenciais */}
                        {formData.workoutType === "swimming" && (
                            <div className="col-12">
                                <div className="form-group mb-3">
                                    <label className="form-label font-size-10 fw-bold text-uppercase text-muted mb-1">
                                        Estilos preferenciais <span className="text-muted fw-normal">(opcional)</span>
                                    </label>
                                    <div className="d-flex flex-wrap gap-2 mt-1">
                                        {WORKOUT_STYLES.map((style) => {
                                            const isActive = formData.styles.includes(style);
                                            return (
                                                <span
                                                    key={style}
                                                    className={`px-3 py-1.5 font-size-11 fw-bold border rounded cursor-pointer transition-all ${isActive ? "bg-primary text-white border-primary shadow-sm" : "bg-light text-dark border-light"}`}
                                                    style={{ cursor: isGenerating ? "default" : "pointer" }}
                                                    onClick={() => !isGenerating && toggleStyle(style)}
                                                >
                                                    {isActive && <i className="mdi mdi-check me-1" />}
                                                    {style}
                                                </span>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Observações */}
                        <div className="col-12">
                            <div className="form-group mb-0">
                                <label className="form-label font-size-10 fw-bold text-uppercase text-muted mb-1">
                                    Observações adicionais <span className="text-muted fw-normal">(opcional)</span>
                                </label>
                                <NeoInput
                                    type="textarea"
                                    rows={2}
                                    value={formData.notes}
                                    onChange={(e) => updateFormField("notes", e.target.value)}
                                    disabled={isGenerating}
                                    placeholder="Ex: Sem borboleta, foco em perna, usar palmar no principal..."
                                    size="sm"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </NeoModal>
    );
};

export default AiWorkoutModal;
